import { ref, reactive, computed, nextTick, watch, type Ref } from "vue";
import { useStore } from "vuex";
import type { FormContext } from "vee-validate";
import {
  CognitoUserCreateInput,
  CognitoUserCountInput,
  CognitoUserSameEmailCountInput,
  ShopListInput,
} from "@/generated/graphql";
import {
  localStorageGetAuthType,
  localStorageGetGroup,
  localStorageGetChainCode,
  localStorageGetCustomerCode,
} from "@/common/localStorageUtil";
import {
  OperationAuthTypeList,
  OperationGroupList,
  GeneralTypeList,
} from "@/common/constant";
import {
  checkChainListDisplay,
  checkPersonalDisabled,
  checkShopListDisplay,
  isOperatorGroupAdmin,
  isOperatorGroupOther,
  isOperatorGroupShop,
} from "@/common/business";
import operatorStore from "@/store/operatorStore";
import type { RadioItem, ChainItem, ShopItem } from "../types";

/** オペレータ登録 フォーム管理に必要な依存をまとめた型。 */
interface UseOperatorRegisterFormDeps {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  overlay: Ref<boolean>;
  updateMode: Ref<boolean>;
  wordList: Ref<Record<string, string>>;
  chainListItems: Ref<ChainItem[]>;
  shopListItems: Ref<ShopItem[]>;
  getChainInfoData: (customerCode: string, chainCode: string) => Promise<any>;
  getShopListData: (input: ShopListInput) => Promise<ShopItem[] | null | undefined>;
  getCognitoUserCountData: (input: CognitoUserCountInput) => Promise<number>;
  getCognitoUserSameEmailCountData: (input: CognitoUserSameEmailCountInput) => Promise<number>;
}

/**
 * オペレータ登録 フォーム状態管理を扱う composable。
 * フォーム入力状態管理、ラジオボタン生成・切替、グループ/権限/チェーン/店舗のカスケード制御、
 * 個人情報制御、IP制限判定、ID/Email重複チェック（onBlur）、vee-validate連携、入力クリアを行う。
 */
export const useOperatorRegisterForm = (deps: UseOperatorRegisterFormDeps) => {
  const {
    errdialog,
    errMessage,
    overlay,
    updateMode,
    wordList,
    chainListItems,
    shopListItems,
    getChainInfoData,
    getShopListData,
    getCognitoUserCountData,
    getCognitoUserSameEmailCountData,
  } = deps;

  // radio,select設定
  const personalRadioItems = GeneralTypeList.possible as RadioItem[];

  // 所属グループ
  const groupRadioItems = ref<RadioItem[]>([{ index: "0", item: "" }]);
  const groupRadioItemsAll = OperationGroupList.other as RadioItem[];
  const groupRadioItemsAdmin = OperationGroupList.shop_admin as RadioItem[];
  const groupRadioItemsShop = OperationGroupList.shop as RadioItem[];

  // 権限
  const authtypeRadioItems = ref<RadioItem[]>([{ index: "0", item: "" }]);
  const authtypeRadioItemsOther = OperationAuthTypeList.other as RadioItem[];
  const authtypeRadioItemsAdmin = OperationAuthTypeList.admin as RadioItem[];
  const authtypeRadioItemsShop = OperationAuthTypeList.shop as RadioItem[];
  const authtypeRadioItemsAll = OperationAuthTypeList.all as RadioItem[];

  // 確認用パスワード
  const comfirm_temporary_password = ref<string>("");
  // 編集モードの登録済みのE-mail
  const update_mode_operator_email = ref<string>("");

  /**
   * オペレータ作成 input
   * ※チェーンコードは処理の中で変更される場合あり
   */
  const cognitUserCreateInput = reactive<CognitoUserCreateInput>({
    auth_type: "",
    chain_code: localStorageGetChainCode(),
    customer_code: localStorageGetCustomerCode(),
    email: "",
    name: "",
    group: "",
    personal_info: personalRadioItems[0].index,
    personal_info_dl: personalRadioItems[0].index,
    phone_number: "0000000000",
    shop_code: "",
    temporary_password: "",
    user_id: "",
    operator_column_email: 0,
    operator_column_tel: 0,
    send_welcome_email: 0,
  });

  /**
   * オペレータ重複IDの確認 input
   */
  const cognitoUserCountInput = reactive<CognitoUserCountInput>({
    customer_code: "",
    id: "",
    name: "",
    shop_code: "",
    group: "",
    chain_code: "",
    auth_type: "",
    include_disabled_user: 0, //無効されたユーザも対象にする
    user_id_exact_match: 1, //完全一致で確認する
  });

  /**
   * オペレータ重複emailの確認 input
   */
  const cognitoUserSameEmailCountInput = reactive<CognitoUserSameEmailCountInput>(
    {
      customer_code: "",
      chain_code: "",
      shop_code: "",
      email: "",
    }
  );

  /**
   * 店舗一覧 input
   */
  const shopListInput = reactive<ShopListInput>({
    chain_code: "",
    customer_code: "",
    limit: 10000,
    offset: 0,
  });

  // 選択された値を格納する変数
  const chainSelectValues = ref<
    { chain_code: string; name: string } | null | undefined
  >({
    chain_code: "",
    name: "",
  });
  const shopSelectValues = ref<
    { shop_code: string; name: string } | null | undefined
  >({
    shop_code: "",
    name: "",
  });
  const groupSelectValues = ref<RadioItem>({ index: "0", item: "" });
  const personalSelectValues = ref<RadioItem>(personalRadioItems[0]);
  const personalDlSelectValues = ref<RadioItem>(personalRadioItems[0]);
  const authtypeSelectValues = ref<RadioItem>({ index: "0", item: "" });
  const operatorFormRef = ref<FormContext | null>(null);

  // チェーン情報を表示するかのフラグ
  const chainViewFlag = ref<boolean>(
    checkChainListDisplay(localStorageGetAuthType())
  );

  // 店舗情報を表示するかのフラグ
  const shopViewFlag = ref<boolean>(false);

  // 個人情報を編集できるかのフラグ
  const personalDisabledFlag = ref<boolean>(
    checkPersonalDisabled(authtypeSelectValues.value.index)
  );

  // 所属グループの状態
  const isOperatorGroupShopFlag = isOperatorGroupShop(localStorageGetGroup());
  const isOperatorGroupAdminFlag = isOperatorGroupAdmin(localStorageGetGroup());
  const isOperatorGroupOtherFlag = isOperatorGroupOther(localStorageGetGroup());
  const isSelectOperatorGroupOther = ref<boolean>(false);

  // メール・電話番号を登録するかのフラグ
  const email_required = ref<number>(0);
  const tel_required = ref<number>(0);

  // IP制限ありチェーンかどうかのフラグ
  const isIpRestrictedChain = ref<boolean>(true);

  // 選択されたオペレータの情報
  const selectedOperatorGroupName = ref<string | undefined>("");
  const selectedOperatorChainName = ref<string | undefined>("");

  // 一覧画面から遷移したか
  const hasOperatorId = computed<boolean>(() => operatorStore.hasValue);

  /**
   * E-mailラベルを取得する
   * IP制限ありチェーン（本部タブ）の場合は「E-mail」、それ以外は「認証用E-mail」
   */
  const emailLabel = computed<string>(() => {
    //本部タブ選択時かつIP制限ありチェーンの場合は「E-mail」
    if (
      groupSelectValues.value &&
      isOperatorGroupAdmin(groupSelectValues.value.index) &&
      isIpRestrictedChain.value
    ) {
      return "E-mail";
    }
    //それ以外は既存のラベル（認証用E-mail）
    return wordList.value.lbl_auth_email || "認証用E-mail";
  });

  // オペーレータ一覧編集関係
  const store = useStore();
  const listEditFlag = computed<boolean>(() => store.getters.listEditFlag);

  const normalizeChainValue = (
    val: { chain_code: string; name: string } | null | undefined
  ): { chain_code: string; name: string } | null => {
    if (!val || val.chain_code.length === 0) {
      return null;
    }
    return val;
  };

  const normalizeShopValue = (
    val: { shop_code: string; name: string } | null | undefined
  ): { shop_code: string; name: string } | null => {
    if (!val || val.shop_code.length === 0) {
      return null;
    }
    return val;
  };

  /**
   * 選択値をvee-validateフォームに同期する。
   */
  const syncSelectionFields = (): void => {
    if (!operatorFormRef.value) {
      return;
    }
    const chainValue = normalizeChainValue(chainSelectValues.value);
    const shopValue = normalizeShopValue(shopSelectValues.value);
    operatorFormRef.value.setFieldValue(
      "operatorGroup",
      groupSelectValues.value,
      true
    );
    operatorFormRef.value.setFieldValue(
      "authType",
      authtypeSelectValues.value,
      true
    );
    operatorFormRef.value.setFieldValue(
      "personalInfoBrowsing",
      personalSelectValues.value,
      true
    );
    operatorFormRef.value.setFieldValue(
      "personalInfoDownload",
      personalDlSelectValues.value,
      true
    );
    operatorFormRef.value.setFieldValue("chainName", chainValue, !!chainValue);
    operatorFormRef.value.setFieldValue("shopName", shopValue, !!shopValue);

    // 編集モード用のフィールドも設定
    if (updateMode.value && listEditFlag.value) {
      // オペレータID（編集モード）
      const operatorIdValue = cognitUserCreateInput.user_id ?? "";
      operatorFormRef.value.setFieldValue(
        "operatorIdEdit",
        operatorIdValue,
        operatorIdValue.length > 0
      );
      // チェーン名（編集モード）
      const chainNameEditValue = selectedOperatorChainName.value ?? "";
      operatorFormRef.value.setFieldValue(
        "chainNameEdit",
        chainNameEditValue,
        chainNameEditValue.length > 0
      );
      // オペレータ名称
      const operatorNameValue = cognitUserCreateInput.name ?? "";
      operatorFormRef.value.setFieldValue(
        "operatorRegistName",
        operatorNameValue,
        operatorNameValue.length > 0
      );
    }
  };

  // 監視：チェーン選択値変更
  watch(
    () => chainSelectValues.value,
    (val) => {
      const chainValue = normalizeChainValue(val);
      operatorFormRef.value?.setFieldValue(
        "chainName",
        chainValue,
        !!chainValue
      );
    },
    { deep: true }
  );

  // 監視：店舗選択値変更
  watch(
    () => shopSelectValues.value,
    (val) => {
      const shopValue = normalizeShopValue(val);
      operatorFormRef.value?.setFieldValue(
        "shopName",
        shopValue,
        !!shopValue
      );
    },
    { deep: true }
  );

  // 監視：email_required変更
  watch(
    () => email_required.value,
    async (val) => {
      if (!operatorFormRef.value) {
        return;
      }
      await nextTick();
      const emailValue = cognitUserCreateInput.email ?? "";
      if (val === 1) {
        operatorFormRef.value.setFieldValue(
          "authEmail",
          emailValue,
          emailValue.length > 0
        );
      } else {
        operatorFormRef.value.setFieldValue("authEmail", "", false);
      }
    }
  );

  /**
   * IP制限の有無を判定する。
   * @param chainCode チェーンコード
   * @returns true: IP制限あり（LOGIN_TYPE='email'なし）、false: IP制限なし（LOGIN_TYPE='email'あり）
   */
  const checkIpRestricted = (chainCode: string): boolean => {
    if (!chainCode) {
      return true; //チェーンコード未選択時はデフォルトでIP制限ありとする
    }
    // chainCode の「-」を「_」に変換し、大文字化して環境変数キーを組み立てる
    const loginTypeKey = "VITE_AWS_LOGIN_TYPE_" + chainCode.replace(/-/g, "_").toUpperCase();
    const loginType = import.meta.env[loginTypeKey];
    //LOGIN_TYPE='email'が設定されている場合はIP制限なし
    return loginType !== "email";
  };

  /**
   * 所属グループのラジオボタンを生成する。
   */
  const createGroupRadio = async (): Promise<void> => {
    if (isOperatorGroupShopFlag) {
      groupRadioItems.value = groupRadioItemsShop;
    } else if (isOperatorGroupAdminFlag) {
      groupRadioItems.value = groupRadioItemsAdmin;
    } else if (isOperatorGroupOtherFlag) {
      groupRadioItems.value = groupRadioItemsAll;
    }
    //選択する
    groupSelectValues.value = groupRadioItems.value[0];
  };

  /**
   * 所属グループクリック @click
   */
  const onClickGroup = async (index: string): Promise<void> => {
    if (!index) {
      return;
    }

    //onClickGroupの実行中にupdateMode（編集モード）が変更されたかを検知するためのスナップショット
    //（fire-and-forget呼び出しでawait中にupdateMode変更 → 古い呼び出しの結果で上書きされるのを防止する）
    const initialUpdateMode = updateMode.value;

    //権限の選択ボックス、チェーンコードを設定する
    if (isOperatorGroupShop(index)) {
      //店舗
      authtypeRadioItems.value = authtypeRadioItemsShop;
      isSelectOperatorGroupOther.value = false;
      if (!updateMode.value) {
        //編集モードではない場合、権限を切替するとチェーンコードをクリアする
        chainSelectValues.value = { chain_code: "", name: "" };
        //本部権限ユーザーの場合、店舗一覧データを再取得する
        //（本部グループ選択時にクリアされた店舗一覧を復元する）
        if (isOperatorGroupAdminFlag) {
          shopSelectValues.value = { shop_code: "", name: "" };
          shopListItems.value = await getShopListData(shopListInput) ?? [];
        }
      }
    } else if (isOperatorGroupAdmin(index)) {
      //本部
      authtypeRadioItems.value = authtypeRadioItemsAdmin;
      isSelectOperatorGroupOther.value = false;
      if (!updateMode.value) {
        //編集モードではない場合、権限を切替するとチェーンコードをクリアする
        chainSelectValues.value = { chain_code: "", name: "" };
        shopSelectValues.value = { shop_code: "", name: "" };
        shopListItems.value = [];
        isIpRestrictedChain.value = true;
      }
    } else {
      //その他
      authtypeRadioItems.value = authtypeRadioItemsOther;
      isSelectOperatorGroupOther.value = true;
    }

    email_required.value = 0;
    tel_required.value = 0;
    cognitUserCreateInput.operator_column_email = 0;
    cognitUserCreateInput.operator_column_tel = 0;
    cognitUserCreateInput.send_welcome_email = 0;

    //ログインユーザが運用管理者の場合、選択されたグループにより設定を変更する
    if (isOperatorGroupOtherFlag) {
      if (isOperatorGroupOther(index)) {
        //運用管理者を選択した場合、チェーンコードを表示しない(固定)
        await setChainCode(localStorageGetChainCode(), false);
        await setShopCode("");
        chainViewFlag.value = false; //チェーンリスト非表示

        //チェーン情報を取得する
        const result = await getChainInfoData(
          localStorageGetCustomerCode(),
          localStorageGetChainCode()
        );

        if (result !== undefined && result !== null) {
          if (result.operator_column_email == 1) {
            email_required.value = 1;
          } else {
            email_required.value = 0;
          }
          if (result.operator_column_tel == 1) {
            tel_required.value = 1;
          } else {
            tel_required.value = 0;
          }

          cognitUserCreateInput.operator_column_email =
            result.operator_column_email;
          cognitUserCreateInput.operator_column_tel = result.operator_column_tel;
          //その他権限はoperator_column_emailと同じ値を設定（既存動作を維持）
          cognitUserCreateInput.send_welcome_email =
            result.operator_column_email;
        }
      } else {
        //運用管理者以外を選択した場合、チェーンコードを表示する
        await setChainCode(localStorageGetChainCode(), false);
        await setShopCode("");
        chainViewFlag.value = true; //チェーンリスト表示
      }
    }

    //ログインユーザが本部権限の場合、チェーン情報を取得してemail/tel表示フラグを設定する
    //（本部権限はチェーンが暗黙的に設定されるため、onChangeChain相当の処理を行う）
    //※編集モード時はupdateOperatorDataがチェーン情報取得とフラグ設定を行うため、ここでは実行しない
    //（onClickGroupはawaitなしで呼ばれるため、編集モード時に実行すると
    //  getChainInfoDataのawait後にupdateOperatorDataが設定した正しい値を上書きしてしまう）
    if (isOperatorGroupAdminFlag && !updateMode.value) {
      const chainCode = localStorageGetChainCode();
      //IP制限の有無を判定
      isIpRestrictedChain.value = checkIpRestricted(chainCode);
      //チェーン情報を取得する
      const result = await getChainInfoData(
        localStorageGetCustomerCode(),
        chainCode
      );

      //await後にupdateMode（編集モード）に切り替わっていた場合、
      //updateOperatorDataが正しい値を設定するため、ここでの設定をスキップする
      if (result !== undefined && result !== null && !updateMode.value) {
        //本部タブ選択時の処理
        if (isOperatorGroupAdmin(index)) {
          //本部タブの場合、常にemail_required=1にする
          email_required.value = 1;
          cognitUserCreateInput.operator_column_email = 1;
          cognitUserCreateInput.send_welcome_email = isIpRestrictedChain.value
            ? 0
            : 1;
        } else {
          //店舗タブの場合、チェーン情報に基づいてemail表示を制御
          if (result.operator_column_email == 1) {
            email_required.value = 1;
          } else {
            email_required.value = 0;
          }
          cognitUserCreateInput.operator_column_email =
            result.operator_column_email;
          cognitUserCreateInput.send_welcome_email =
            result.operator_column_email;
        }
        if (result.operator_column_tel == 1) {
          tel_required.value = 1;
        } else {
          tel_required.value = 0;
        }
        cognitUserCreateInput.operator_column_tel = result.operator_column_tel;
      }
    }

    authtypeSelectValues.value = authtypeRadioItems.value[0];

    //オペレータIDをクリアする（バリデーションを実行してmeta.validを更新し、untouched状態にしてエラー表示を抑制）
    if (!updateMode.value) {
      cognitUserCreateInput.user_id = "";
      operatorFormRef.value?.setFieldValue("operatorId", "", true);
      operatorFormRef.value?.setFieldTouched("operatorId", false);
    }

    //店舗情報リストを表示するか設定する
    //（onClickGroupはfire-and-forgetで呼ばれるため、実行中にupdateModeが変更された場合は
    //  updateOperatorData側のonClickGroup呼び出しが正しい値を設定するので、ここでは更新しない）
    if (updateMode.value === initialUpdateMode) {
      shopViewFlag.value = checkShopListDisplay(index);
    }

    //個人情報表示制御のチェックをする
    await checkPersonal(authtypeSelectValues.value.index);

    // 所属グループ切替時にバリデーションエラーをクリアする
    await nextTick();
    if (operatorFormRef.value) {
      operatorFormRef.value.resetForm({ values: operatorFormRef.value.values });
    }
  };

  /**
   * 権限クリック @click
   */
  const onClickAuthType = async (index: string): Promise<void> => {
    //個人情報表示制御のチェックをする
    await checkPersonal(index);
  };

  /**
   * 個人情報表示制御のチェック。
   */
  const checkPersonal = async (index: string): Promise<void> => {
    if (checkPersonalDisabled(index)) {
      //管理者は個人情報を不可にする
      personalSelectValues.value = personalRadioItems[0];
      personalDlSelectValues.value = personalRadioItems[0];

      //個人情報を選択不可にする
      personalDisabledFlag.value = true;
    } else {
      //個人情報を選択可にする
      personalDisabledFlag.value = false;
    }

    syncSelectionFields();
  };

  /**
   * 「チェーン」選択ボックス @update:modelValue
   */
  const onChangeChain = async (): Promise<void> => {
    try {
      //チェーンがクリアされた場合、店舗情報をクリアする（バリデーションを実行してmeta.validを更新し、untouched状態にしてエラー表示を抑制）
      if (!chainSelectValues.value) {
        shopListItems.value = [];
        shopSelectValues.value = { shop_code: "", name: "" };
        operatorFormRef.value?.setFieldValue("shopName", null, true);
        operatorFormRef.value?.setFieldTouched("shopName", false);
        cognitUserCreateInput.shop_code = "";
        cognitoUserCountInput.shop_code = "";
        email_required.value = 0;
        tel_required.value = 0;
        cognitUserCreateInput.operator_column_email = 0;
        cognitUserCreateInput.operator_column_tel = 0;
        cognitUserCreateInput.send_welcome_email = 0;
        isIpRestrictedChain.value = true;
        return;
      }

      if (shopListInput.chain_code !== chainSelectValues.value.chain_code) {
        shopSelectValues.value = { shop_code: "", name: "" };
        operatorFormRef.value?.setFieldValue("shopName", null, true);
        operatorFormRef.value?.setFieldTouched("shopName", false);
        cognitUserCreateInput.shop_code = "";
        cognitoUserCountInput.shop_code = "";
      }

      //IP制限の有無を判定
      isIpRestrictedChain.value = checkIpRestricted(
        chainSelectValues.value!.chain_code
      );

      //チェーン情報を取得する
      const result = await getChainInfoData(
        localStorageGetCustomerCode(),
        chainSelectValues.value!.chain_code
      );

      if (result !== undefined && result !== null) {
        //本部タブ選択時の処理
        if (
          groupSelectValues.value &&
          isOperatorGroupAdmin(groupSelectValues.value.index)
        ) {
          //本部タブの場合、チェーン選択時は常にemail_required=1にする
          email_required.value = 1;
          //本部タブの場合、operator_column_emailも1に設定（Cognitoにemailを保存するため）
          cognitUserCreateInput.operator_column_email = 1;
          //本部タブ + IP制限ありチェーンの場合はウェルカムメールを送信しない
          //本部タブ + IP制限なしチェーン（認証用E-mail）の場合はウェルカムメールを送信する
          cognitUserCreateInput.send_welcome_email = isIpRestrictedChain.value
            ? 0
            : 1;
        } else {
          //本部タブ以外は既存の動作を維持
          if (result.operator_column_email == 1) {
            email_required.value = 1;
          } else {
            email_required.value = 0;
          }
          cognitUserCreateInput.operator_column_email =
            result.operator_column_email;
          //本部タブ以外はoperator_column_emailと同じ値を設定（既存動作を維持）
          cognitUserCreateInput.send_welcome_email =
            result.operator_column_email;
        }
        if (result.operator_column_tel == 1) {
          tel_required.value = 1;
        } else {
          tel_required.value = 0;
        }

        cognitUserCreateInput.operator_column_tel = result.operator_column_tel;
      }

      //チェーンコードを設定
      await setChainCode(chainSelectValues.value!.chain_code, true);
      //店舗情報を取得する
      shopListItems.value = await getShopListData(shopListInput) ?? [];

      await onBlurEmail();
    } catch (err: unknown) {
      errdialog.value = true;

      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "不明なエラーが発生しました。";
        console.error("未知のエラー:", err);
      }

      return;
    }
  };

  /**
   * チェーンコードを設定する。
   */
  const setChainCode = async (
    chainCode: string,
    selectUpdate: boolean
  ): Promise<void> => {
    if (chainCode.length > 0) {
      shopListInput.chain_code = chainCode;
      cognitUserCreateInput.chain_code = chainCode;
      cognitoUserCountInput.chain_code = chainCode;
    }
    if (selectUpdate && chainListItems.value) {
      chainSelectValues.value = chainListItems.value.find(
        (element) => element && element.chain_code === chainCode
      ) as { chain_code: string; name: string } | null | undefined;
    }
  };

  /**
   * 店舗コードを設定する。
   */
  const setShopCode = async (shopCode: string): Promise<void> => {
    if (shopCode.length == 0) {
      return;
    }
    cognitUserCreateInput.shop_code = shopCode;
    cognitoUserCountInput.shop_code = shopCode;
    shopSelectValues.value = (shopListItems.value || [])!.find(
      (element) => element && element.shop_code === shopCode
    ) as { shop_code: string; name: string } | null | undefined;
  };

  /**
   * 認証emailフォーカスアウト @blur
   */
  const onBlurEmail = async (): Promise<void> => {
    overlay.value = true;
    try {
      //[その他]所属がその他の場合、emailが入力されてなければチェックしない
      if (
        isOperatorGroupOtherFlag &&
        isSelectOperatorGroupOther.value &&
        (!cognitUserCreateInput.email ||
          !cognitUserCreateInput.chain_code ||
          !cognitUserCreateInput.customer_code)
      ) {
        return;
      }
      //[その他]
      if (
        isOperatorGroupOtherFlag &&
        !isSelectOperatorGroupOther.value &&
        updateMode.value &&
        chainSelectValues.value &&
        cognitUserCreateInput.email == ""
      ) {
        return;
      }
      //[その他]所属がその他以外の場合、チェーンコードが入力されてなければチェックしない
      if (
        isOperatorGroupOtherFlag &&
        !isSelectOperatorGroupOther.value &&
        !updateMode.value &&
        chainSelectValues.value &&
        (chainSelectValues.value.chain_code == "" || !cognitUserCreateInput.email)
      ) {
        return;
      }
      //[本部管理者]
      if (isOperatorGroupAdminFlag && !cognitUserCreateInput.email) {
        return;
      }

      //本部タブ選択時かつIP制限ありチェーンの場合は重複チェックをスキップ
      if (
        groupSelectValues.value &&
        isOperatorGroupAdmin(groupSelectValues.value.index) &&
        isIpRestrictedChain.value
      ) {
        return;
      }

      if (
        updateMode.value &&
        update_mode_operator_email.value == cognitUserCreateInput.email
      ) {
        return;
      } else {
        cognitoUserSameEmailCountInput.customer_code =
          cognitUserCreateInput.customer_code;
        cognitoUserSameEmailCountInput.chain_code =
          cognitUserCreateInput.chain_code;
        cognitoUserSameEmailCountInput.email = cognitUserCreateInput.email ?? "";
        // Cogniteユーザに存在しているかチェック
        const count = await getCognitoUserSameEmailCountData(
          cognitoUserSameEmailCountInput
        );

        //既に存在している場合エラー
        if (count > 0) {
          errdialog.value = true;
          errMessage.value =
            "認証用E-mail " +
            cognitoUserSameEmailCountInput.email +
            " は既に存在しています。";
          cognitoUserSameEmailCountInput.email = cognitUserCreateInput.email = "";
          // vee-validateのフィールド値もクリアして入力欄を空にする
          operatorFormRef.value?.setFieldValue("authEmail", "");
          return;
        }
      }
    } catch (err: unknown) {
      errdialog.value = true;

      if (err instanceof Error) {
        console.error(err.message);
      } else {
        console.error("未知のエラー:", err);
      }

      errMessage.value = "認証用E-mail情報の取得に失敗しました";
      return;
    } finally {
      overlay.value = false;
    }
  };

  /**
   * オペレータIDフォーカスアウト @blur
   */
  const onBlurOperatorId = async (): Promise<void> => {
    overlay.value = true;
    try {
      if (!cognitUserCreateInput.user_id) {
        return;
      }

      //use_idを設定
      cognitoUserCountInput.id = cognitUserCreateInput.user_id;

      // Cogniteユーザに存在しているかチェック
      const count = await getCognitoUserCountData(cognitoUserCountInput);

      //既に存在している場合エラー
      if (count > 0) {
        errdialog.value = true;
        errMessage.value =
          "オペレータID " +
          cognitUserCreateInput.user_id +
          " は既に存在しています。";
        cognitUserCreateInput.user_id = "";
        //vee-validateのフォームフィールドもクリアする
        operatorFormRef.value?.setFieldValue("operatorId", "", true);
        return;
      }

      //use_idを設定
      cognitoUserCountInput.id = cognitUserCreateInput.user_id;
      //無効化されたオペレータも対象にする
      cognitoUserCountInput.include_disabled_user = 1;

      //既に存在している場合エラー
      if ((await getCognitoUserCountData(cognitoUserCountInput)) > 0) {
        errdialog.value = true;
        errMessage.value =
          "すでに削除されたオペレータID " +
          cognitUserCreateInput.user_id +
          " は利用できません。";
        cognitUserCreateInput.user_id = "";
        //vee-validateのフォームフィールドもクリアする
        operatorFormRef.value?.setFieldValue("operatorId", "", true);
        cognitoUserCountInput.id = "";
        cognitoUserCountInput.include_disabled_user = 0;
        return;
      }
      //デフォルト設定に戻す
      cognitoUserCountInput.include_disabled_user = 0;
    } catch (err: unknown) {
      errdialog.value = true;

      if (err instanceof Error) {
        console.error(err.message);
      } else {
        console.error("未知のエラー:", err);
      }

      errMessage.value = "ユーザ情報の取得に失敗しました";
      return;
    } finally {
      overlay.value = false;
    }
  };

  /**
   * 入力項目をクリアする。
   */
  const allClear = async (): Promise<void> => {
    cognitUserCreateInput.chain_code = "";
    cognitUserCreateInput.user_id = "";
    cognitUserCreateInput.name = "";
    cognitUserCreateInput.group = "0";
    cognitUserCreateInput.personal_info = personalRadioItems[0].index;
    cognitUserCreateInput.personal_info_dl = personalRadioItems[0].index;
    cognitUserCreateInput.email = "";
    cognitUserCreateInput.temporary_password = "";
    cognitUserCreateInput.operator_column_email = 0;
    cognitUserCreateInput.operator_column_tel = 0;
    cognitUserCreateInput.send_welcome_email = 0;
    comfirm_temporary_password.value = "";
    chainSelectValues.value = { chain_code: "", name: "" };
    shopSelectValues.value = { shop_code: "", name: "" };
    groupSelectValues.value = { index: "", item: "" };
    personalSelectValues.value = personalRadioItems[0];
    personalDlSelectValues.value = personalRadioItems[0];
    authtypeRadioItems.value = authtypeRadioItemsShop;
    authtypeSelectValues.value = authtypeRadioItems.value[0];

    // vee-validateのフォームフィールドをリセット
    if (operatorFormRef.value) {
      operatorFormRef.value.resetForm();
    }
  };

  return {
    personalRadioItems,
    cognitUserCreateInput,
    cognitoUserCountInput,
    cognitoUserSameEmailCountInput,
    shopListInput,
    chainSelectValues,
    shopSelectValues,
    groupSelectValues,
    personalSelectValues,
    personalDlSelectValues,
    authtypeSelectValues,
    operatorFormRef,
    groupRadioItems,
    authtypeRadioItems,
    chainViewFlag,
    shopViewFlag,
    personalDisabledFlag,
    isSelectOperatorGroupOther,
    isOperatorGroupOtherFlag,
    hasOperatorId,
    email_required,
    tel_required,
    isIpRestrictedChain,
    selectedOperatorGroupName,
    selectedOperatorChainName,
    comfirm_temporary_password,
    update_mode_operator_email,
    emailLabel,
    listEditFlag,
    checkIpRestricted,
    createGroupRadio,
    onClickGroup,
    onClickAuthType,
    checkPersonal,
    onChangeChain,
    setChainCode,
    setShopCode,
    onBlurEmail,
    onBlurOperatorId,
    syncSelectionFields,
    allClear,
  };
};
